/**
 * Server Error component for video share pages
 * Displays error details for 500+ errors and network issues
 */

import { Box, Container, Paper, Alert, Typography, Button } from "@mui/material";

interface VideoServerErrorProps {
  error: Error | unknown;
}

export function VideoServerError({ error }: VideoServerErrorProps) {
  const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: (theme) => theme.palette.grey[100],
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={3}
          sx={{
            p: { xs: 4, md: 6 },
            borderRadius: 3,
          }}
        >
          <Alert
            severity="error"
            sx={{
              mb: 3,
              "& .MuiAlert-message": {
                width: "100%"
              }
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Something Went Wrong
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              We encountered an error while loading this video:
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontFamily: "monospace",
                backgroundColor: "rgba(0, 0, 0, 0.05)",
                p: 1,
                borderRadius: 1,
                fontSize: "0.875rem",
                wordBreak: "break-word",
              }}
            >
              {errorMessage}
            </Typography>
          </Alert>

          {/* Action buttons */}
          <Box sx={{ display: "flex", gap: 2, flexDirection: "column" }}>
            <Button
              variant="contained"
              fullWidth
              onClick={() => window.location.reload()}
              sx={{ py: 1.5 }}
            >
              Try Again
            </Button>
            <Typography
              variant="body2"
              align="center"
              sx={{ color: "text.secondary", mt: 2 }}
            >
              If the problem persists, please contact the person who shared this link.
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}
